/**
 * useWistiaVideo Hook
 * Custom hook for managing Wistia video loading and initialization
 */

import { useState, useEffect, useRef } from 'react';
import { VIDEO_CONFIG } from '../constants/videoConfig';
import {
  loadWistiaScript,
  initializeWistiaVideo,
  createVideoContainer,
} from '../utils/videoHelpers';

export const useWistiaVideo = (viewportSize, { enabled = true } = {}) => {
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const videoContainerRef = useRef(null);

  // Load Wistia video
  useEffect(() => {
    if (!enabled) {
      setIsVideoLoading(false);
      if (videoContainerRef.current) {
        videoContainerRef.current.innerHTML = '';
      }
      return undefined;
    }

    const videoId =
      viewportSize.width <= VIDEO_CONFIG.MOBILE_BREAKPOINT
        ? VIDEO_CONFIG.MOBILE_VIDEO_ID
        : VIDEO_CONFIG.DESKTOP_VIDEO_ID;

    setIsVideoLoading(true);
    let isMounted = true;

    // Clear previous video
    if (videoContainerRef.current) {
      videoContainerRef.current.innerHTML = '';
    }

    // Create and append video container
    const videoContainer = createVideoContainer(videoId);
    if (videoContainerRef.current) {
      videoContainerRef.current.appendChild(videoContainer);
    }

    // Load script and initialize video
    loadWistiaScript(isMounted)
      .then(() => initializeWistiaVideo(videoId, isMounted, setIsVideoLoading))
      .catch(error => {
        console.error('Failed to initialize video:', error);
        if (isMounted) setIsVideoLoading(false);
      });

    // Cleanup
    return () => {
      isMounted = false;
      if (videoContainerRef.current) {
        videoContainerRef.current.innerHTML = '';
      }
    };
  }, [enabled, viewportSize.width]);

  return {
    videoContainerRef,
    isVideoLoading,
  };
};

export default useWistiaVideo;
